import React from 'react';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo } from '../mock';

export const Contact = () => {
  const contactMethods = [
    {
      icon: Phone,
      title: 'Phone',
      info: companyInfo.phone,
      description: 'Mon-Sat, 9:00 AM - 6:00 PM',
      link: `tel:${companyInfo.phone.split('/')[0].trim()}`,
      action: 'Call Now'
    },
    {
      icon: Mail,
      title: 'Email',
      info: companyInfo.email,
      description: 'We reply within 24 hours',
      link: `mailto:${companyInfo.email}`,
      action: 'Send Email'
    },
    {
      icon: MapPin,
      title: 'Address',
      info: companyInfo.address,
      description: 'Visit us at our office',
      link: null,
      action: null
    }
  ];

  const businessHours = [
    { day: 'Monday - Saturday', hours: '9:00 AM - 6:00 PM' },
    { day: 'Sunday', hours: 'Closed' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
              Get In Touch
            </h1>
            <p className="text-xl text-slate-600">
              Have questions or need a quote? We're here to help. Reach out to us through any of the channels below.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {contactMethods.map((method, index) => {
              const Icon = method.icon;
              return (
                <Card
                  key={index}
                  className="border-2 border-slate-200 hover:border-orange-200 hover:shadow-xl transition-all duration-300 group"
                >
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform">
                      <Icon className="w-8 h-8 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">
                      {method.title}
                    </h3>
                    <p className="text-slate-900 font-medium mb-2">{method.info}</p>
                    <p className="text-sm text-slate-600 mb-6">{method.description}</p>
                    {method.link && (
                      <a href={method.link}>
                        <Button className="bg-slate-900 hover:bg-slate-800 text-white w-full">
                          {method.action}
                        </Button>
                      </a>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Contact Information Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Business Hours */}
            <Card className="border-2 border-slate-200">
              <CardContent className="p-10">
                <div className="flex items-center space-x-3 mb-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-slate-700 to-slate-900 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Business Hours</h2>
                </div>
                <div className="space-y-4">
                  {businessHours.map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center py-3 border-b border-slate-100 last:border-0"
                    >
                      <span className="text-slate-700 font-medium">{item.day}</span>
                      <span className="text-slate-900 font-semibold">{item.hours}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Additional Info */}
            <Card className="border-2 border-slate-200">
              <CardContent className="p-10">
                <div className="flex items-center space-x-3 mb-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg flex items-center justify-center">
                    <Send className="w-6 h-6 text-orange-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Quick Contact</h2>
                </div>
                <div className="space-y-6">
                  <div>
                    <div className="text-sm font-semibold text-slate-500 mb-2">
                      For Sales Inquiries
                    </div>
                    <a
                      href={`mailto:${companyInfo.email}`}
                      className="text-lg text-orange-600 hover:text-orange-700 font-medium"
                    >
                      {companyInfo.email}
                    </a>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-500 mb-2">
                      Phone Support
                    </div>
                    <a
                      href={`tel:${companyInfo.phone.split('/')[0].trim()}`}
                      className="text-lg text-orange-600 hover:text-orange-700 font-medium"
                    >
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Additional Contact Information */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-slate-900 mb-6 text-center">
              Visit Our Office
            </h2>
            <p className="text-lg text-slate-600 mb-8 text-center">
              We welcome you to visit our office for detailed discussions about your chemical requirements. Our team of experts is ready to assist you with technical specifications, quality certifications, and competitive pricing.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="border-2 border-slate-200">
                <CardContent className="p-8">
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <MapPin className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                      <div>
                        <div className="font-semibold text-slate-900 mb-1">Location</div>
                        <div className="text-slate-600">{companyInfo.address}</div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <Phone className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                      <div>
                        <div className="font-semibold text-slate-900 mb-1">Phone</div>
                        <a
                          href={`tel:${companyInfo.phone.split('/')[0].trim()}`}
                          className="text-slate-600 hover:text-orange-600"
                        >
                          {companyInfo.phone}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <Mail className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                      <div>
                        <div className="font-semibold text-slate-900 mb-1">Email</div>
                        <a
                          href={`mailto:${companyInfo.email}`}
                          className="text-slate-600 hover:text-orange-600"
                        >
                          {companyInfo.email}
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100">
                <CardContent className="p-8 flex items-center justify-center h-full">
                  <div className="text-center">
                    <MapPin className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium text-lg">Bhayander West</p>
                    <p className="text-slate-500 mt-2">Thane, Maharashtra</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Place an Order?
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Contact us today for competitive quotes and personalized chemical solutions
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`mailto:${companyInfo.email}`}>
              <Button
                size="lg"
                className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg h-auto"
              >
                <Mail className="mr-2 w-5 h-5" />
                Email Us
              </Button>
            </a>
            <a href={`tel:${companyInfo.phone.split('/')[0].trim()}`}>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg h-auto"
              >
                <Phone className="mr-2 w-5 h-5" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};